import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getContractorProjects,
  getProjectFiles,
  getProjectLogs,
  getProjects,
} from "../api";
import { AppDispatch, RootState } from "../store";
import { Roles } from "../../types/roles";
import {
  ConvertedGeometry,
  ConvertedGeometryByStage,
  GeometryByStage,
  ProjectLog,
  ProjectsResponse,
  SelectedFeature,
  StageName,
  StageType,
  TerritoryType,
  TransformedProjectsData,
  UploadedFile,
  UploadedFiles,
} from "../../interfaces/utilities";
import { formatProject, sortProjects } from "../../utils/helpers-for-utilities";
import { editFeaturesOnMap } from "../actions/master-plan-modification";
import Graphic from "@arcgis/core/Graphic";
import { setConvertedFeatures } from "../actions/map";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { getGeometryType } from "../../utils/helpers-for-2d-map";
import { v4 as uuidv4 } from "uuid";

const stageKeyMap: Record<StageName, StageType> = {
  "Design Stage": "design",
  "As-Built": "asBuilt",
};

export const getStageKey = (stage: StageName): StageType => stageKeyMap[stage];

export const stages: StageType[] = ["design", "asBuilt"];
export const territoryTypes: TerritoryType[] = ["stage", "building"];

interface InitialState {
  fullData: TransformedProjectsData[];
  data: TransformedProjectsData[];
  totalDataSize: number;
  isLoading: boolean;
  isMapReadyUtilities: boolean;
  selectedRow: TransformedProjectsData | null;
  rawData: ProjectsResponse[];
  uploadedFiles: UploadedFiles;
  convertedGeometry: ConvertedGeometryByStage;
  selectedFeature: SelectedFeature | null;
  visibleEditAttributes: boolean;
  stageTerritoryGeometry: GeometryByStage;
  buildingTerritoryGeometry: GeometryByStage;
  deletedFileIds: number[];
  isLoadingProjectFiles: boolean;
  projectLog: ProjectLog[];
  mainOperationFeature: SelectedFeature | null;
  secondaryOperationFeature: SelectedFeature | null;
  fileHidden: Record<StageType, Record<string, boolean>>;
  receiveNotifications: boolean;
  notificationCount: number;
  hiddenTerritories: Record<TerritoryType, Record<StageType, boolean>>;
  drawnGraphics: Graphic[];
  filesLoadedFromServer: Record<
    StageType,
    {
      loaded: boolean;
      stageId: number | null;
    }
  >;
}

const initialState: InitialState = {
  fullData: [],
  data: [],
  totalDataSize: 0,
  isLoading: false,
  isMapReadyUtilities: false,
  selectedRow: null,
  rawData: [],
  uploadedFiles: {
    design: [],
    asBuilt: [],
  },
  convertedGeometry: {
    design: null,
    asBuilt: null,
  },
  selectedFeature: null,
  visibleEditAttributes: false,
  stageTerritoryGeometry: {
    design: null,
    asBuilt: null,
  },
  buildingTerritoryGeometry: {
    design: null,
    asBuilt: null,
  },
  deletedFileIds: [],
  isLoadingProjectFiles: false,
  projectLog: [],
  mainOperationFeature: null,
  secondaryOperationFeature: null,
  fileHidden: {
    design: {},
    asBuilt: {},
  },
  receiveNotifications: false,
  notificationCount: 0,
  hiddenTerritories: {
    stage: {
      design: false,
      asBuilt: false,
    },
    building: {
      design: false,
      asBuilt: false,
    },
  },
  drawnGraphics: [],
  filesLoadedFromServer: {
    design: {
      loaded: false,
      stageId: null,
    },
    asBuilt: {
      loaded: false,
      stageId: null,
    },
  },
};

export const fetchUtilities = createAsyncThunk(
  "masterUtilities/fetchUtilities",
  async (_, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const roles = state.user.user?.roles;
    const userCities = state.user.user?.cities;
    const regNumber = state.user.user?.regNumber;
    const isContractor = roles?.includes(Roles.Contractor) ?? false;

    let items: ProjectsResponse[];

    if (isContractor && regNumber) {
      items = await getContractorProjects(regNumber);
    } else {
      items = await getProjects().then((res) =>
        Array.isArray(userCities) && userCities.length > 0
          ? res.filter(
              (item) =>
                item?.workPermitEstateId &&
                userCities.includes(item.workPermitEstateId),
            )
          : res,
      );
    }

    return { items, isContractor };
  },
);

export const fetchProjectFiles = createAsyncThunk<
  void,
  number,
  { dispatch: AppDispatch }
>("masterUtilities/fetchProjectFiles", async (projectId, { dispatch }) => {
  const result = await getProjectFiles(projectId);

  result.forEach((item) => {
    const stageType: StageType = item.stageName.toLowerCase().includes("design")
      ? "design"
      : "asBuilt";
    console.log(item);
    const uploaded: UploadedFile[] = [];
    const geometryPayload: ConvertedGeometry = {};

    item.files.forEach((f) => {
      const filename = f.name;
      const id = f.fileId;
      const dotIndex = filename.lastIndexOf(".");
      const name = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
      const extension =
        dotIndex >= 0 && dotIndex < filename.length - 1
          ? filename.slice(dotIndex + 1).toLowerCase()
          : "";

      uploaded.push({ id, name, extension });
      geometryPayload[name] = {
        graphics: f.graphics as any,
        fields: f.fields,
        fileName: f.name,
        fileId: id,
      };
    });

    if (item.territories && item.territories.length > 0) {
      const territory = item.territories[0];

      const stageGeometry = territory.stageTerritoryGeometry
        ? new Graphic({
            geometry: getGeometryType(
              JSON.parse(territory.stageTerritoryGeometry),
            ),
          })
        : null;

      const buildingGeometry = territory.buildingTerritoryGeometry
        ? new Graphic({
            geometry: getGeometryType(
              JSON.parse(territory.buildingTerritoryGeometry),
            ),
          })
        : null;

      if (stageGeometry) {
        dispatch(
          setStageTerritoryGeometry({ stageType, geometry: stageGeometry }),
        );
      }

      if (buildingGeometry) {
        dispatch(
          setBuildingTerritoryGeometry({
            stageType,
            geometry: buildingGeometry,
          }),
        );
      }
    }

    dispatch(setUploadedFiles({ stage: stageType, files: uploaded }));
    dispatch(
      setConvertedGeometry({ stage: stageType, geometry: geometryPayload }),
    );
    dispatch(
      setFilesLoadedFromServer({
        stage: stageType,
        loaded: true,
        stageId: item.stageId,
      }),
    );
    dispatch(setConvertedFeatures());
  });
});

export const fetchProjectLogs = createAsyncThunk<ProjectLog[], number>(
  "masterUtilities/fetchProjectLogs",
  async (projectId) => {
    return getProjectLogs(projectId);
  },
);

export const masterUtilitiesSlice = createSlice({
  name: "masterUtilities",
  initialState,
  reducers: {
    setData: (state, { payload }) => {
      state.data = payload.data;
      state.totalDataSize = payload.totalDataSize;
    },
    setIsMapReadyUtilities: (state, action: PayloadAction<boolean>) => {
      state.isMapReadyUtilities = action.payload;
    },
    setSelectedRow: (state, action: PayloadAction<TransformedProjectsData>) => {
      state.selectedRow = action.payload;
    },
    setUploadedFiles: (
      state,
      action: PayloadAction<{ stage: StageType; files: UploadedFile[] }>,
    ) => {
      const { stage, files } = action.payload;
      state.uploadedFiles[stage].push(...files);
    },
    setConvertedGeometry: (
      state,
      action: PayloadAction<{ stage: StageType; geometry: ConvertedGeometry }>,
    ) => {
      const { stage, geometry } = action.payload;

      for (const file of Object.values(geometry)) {
        file.graphics.forEach((g) => {
          if (!g.attributes.uuid) {
            g.attributes.uuid = uuidv4();
          }
        });
      }

      if (state.convertedGeometry[stage] === null) {
        state.convertedGeometry[stage] = geometry;
      } else {
        state.convertedGeometry[stage] = {
          ...state.convertedGeometry[stage]!,
          ...geometry,
        };
      }
    },
    removeUploadedFileAndGeometry: (
      state,
      action: PayloadAction<{
        stage: StageType | undefined;
        name: string;
      }>,
    ) => {
      const { stage, name } = action.payload;
      if (!stage) return;

      const fileToDelete = state.uploadedFiles[stage].find(
        (f) => f.name === name,
      );
      if (
        fileToDelete?.id !== undefined &&
        !state.deletedFileIds.includes(fileToDelete.id)
      ) {
        state.deletedFileIds.push(fileToDelete.id);
      }

      state.uploadedFiles[stage] = state.uploadedFiles[stage].filter(
        (f) => f.name !== name,
      );

      const stageGeometry = state.convertedGeometry[stage];

      if (stageGeometry) {
        delete stageGeometry[name];
      }

      if (state.selectedFeature && state.selectedFeature.layerName === name) {
        state.selectedFeature = null;
        state.visibleEditAttributes = false;
      }

      if (state.mainOperationFeature?.layerName === name) {
        state.mainOperationFeature = null;
      }
      if (state.secondaryOperationFeature?.layerName === name) {
        state.secondaryOperationFeature = null;
      }
    },
    setSelectedFeature: (state, action: PayloadAction<any>) => {
      state.selectedFeature = action.payload;
    },
    setVisibleEditAttributes: (state, action: PayloadAction<boolean>) => {
      state.visibleEditAttributes = action.payload;
    },
    updateFeatureAttributes: (
      state,
      action: PayloadAction<{
        layerName: string;
        objectId: number;
        newAttributes: Record<string, any>;
        stage: StageType | undefined;
      }>,
    ) => {
      const { layerName, objectId, newAttributes, stage } = action.payload;
      if (!stage) return;

      const stageGeometry = state.convertedGeometry[stage];
      if (!stageGeometry) return;

      const layerData = stageGeometry[layerName];
      if (!layerData) return;

      layerData.graphics = layerData.graphics.map((graphic: any) => {
        if (graphic.attributes.uuid === objectId) {
          graphic.attributes = {
            ...graphic.attributes,
            ...newAttributes,
          };

          if (
            state.selectedFeature &&
            state.selectedFeature.layerName === layerName &&
            state.selectedFeature.attributes.uuid === objectId
          ) {
            state.selectedFeature = {
              layerName,
              attributes: graphic.attributes,
              graphic,
              isArea: graphic.geometry?.type === "polygon",
              stage,
            };
          }
        }

        return graphic;
      });
    },
    setFilesLoadedFromServer: (
      state,
      action: PayloadAction<{
        stage: StageType;
        loaded: boolean;
        stageId: number;
      }>,
    ) => {
      const { stage, loaded, stageId } = action.payload;
      state.filesLoadedFromServer[stage] = {
        loaded,
        stageId,
      };
    },
    resetEditAttributesState: (state) => {
      state.uploadedFiles = initialState.uploadedFiles;
      state.convertedGeometry = initialState.convertedGeometry;
      state.selectedFeature = initialState.selectedFeature;
      state.visibleEditAttributes = initialState.visibleEditAttributes;
      state.selectedRow = initialState.selectedRow;
    },
    resetTableState: (state) => {
      state.rawData = initialState.rawData;
      state.fullData = initialState.fullData;
      state.data = initialState.data;
      state.totalDataSize = initialState.totalDataSize;
    },
    removeGraphicFromConvertedGeometry: (
      state,
      action: PayloadAction<{
        objectId: string;
        stage: StageType | undefined;
      }>,
    ) => {
      const { objectId, stage } = action.payload;
      if (!stage) return;

      const stageGeometry = state.convertedGeometry[stage];
      if (!stageGeometry) return;

      for (const [layerName, layerData] of Object.entries(stageGeometry)) {
        const initialLength = layerData.graphics.length;

        layerData.graphics = layerData.graphics.filter(
          (graphic: any) => graphic.attributes?.uuid !== objectId,
        );

        if (layerData.graphics.length < initialLength) {
          if (
            state.selectedFeature &&
            state.selectedFeature.layerName === layerName &&
            state.selectedFeature.attributes?.uuid === objectId
          ) {
            state.selectedFeature = null;
            state.visibleEditAttributes = false;
          }
          if (
            state.mainOperationFeature?.layerName === layerName &&
            state.mainOperationFeature.attributes?.uuid === objectId
          ) {
            state.mainOperationFeature = null;
          }

          if (
            state.secondaryOperationFeature?.layerName === layerName &&
            state.secondaryOperationFeature.attributes?.uuid === objectId
          ) {
            state.secondaryOperationFeature = null;
          }
          break;
        }
      }
    },
    setStageTerritoryGeometry: (
      state,
      action: PayloadAction<{ stageType: StageType; geometry: Graphic | null }>,
    ) => {
      const { stageType, geometry } = action.payload;

      if (geometry) {
        const uuid = geometry.attributes?.uuid;
        state.drawnGraphics = state.drawnGraphics.filter(
          (g) => g.attributes?.uuid !== uuid,
        );
      }

      state.stageTerritoryGeometry[stageType] = geometry;
    },
    setBuildingTerritoryGeometry: (
      state,
      action: PayloadAction<{ stageType: StageType; geometry: Graphic | null }>,
    ) => {
      const { stageType, geometry } = action.payload;

      if (geometry) {
        const uuid = geometry.attributes?.uuid;
        state.drawnGraphics = state.drawnGraphics.filter(
          (g) => g.attributes?.uuid !== uuid,
        );
      }

      state.buildingTerritoryGeometry[stageType] = geometry;
    },
    resetMasterUtilitiesState: () => initialState,
    setMainOperationFeature: (
      state,
      action: PayloadAction<SelectedFeature | null>,
    ) => {
      state.mainOperationFeature = action.payload;
    },
    setSecondaryOperationFeature: (
      state,
      action: PayloadAction<SelectedFeature | null>,
    ) => {
      state.secondaryOperationFeature = action.payload;
    },
    updateConvertedGeometryFeature: (
      state,
      action: PayloadAction<{
        stage: StageType;
        feature: SelectedFeature;
      }>,
    ) => {
      const { stage, feature } = action.payload;
      const uuid = feature.attributes?.uuid;
      if (!uuid || !state.convertedGeometry[stage]) return;

      const stageGeometry = state.convertedGeometry[stage]!;

      for (const [, layer] of Object.entries(stageGeometry)) {
        const targetGraphic = layer.graphics.find(
          (g) => g.attributes?.uuid === uuid,
        );
        if (targetGraphic) {
          (targetGraphic as any).geometry = JSON.stringify(
            feature.graphic.geometry,
          );
          break;
        }
      }
    },
    updateStageTerritoryFeature: (
      state,
      action: PayloadAction<{
        stage: StageType;
        feature: SelectedFeature;
      }>,
    ) => {
      const { stage, feature } = action.payload;
      state.stageTerritoryGeometry[stage] = feature.graphic;
    },
    updateBuildingTerritoryFeature: (
      state,
      action: PayloadAction<{
        stage: StageType;
        feature: SelectedFeature;
      }>,
    ) => {
      const { stage, feature } = action.payload;
      state.buildingTerritoryGeometry[stage] = feature.graphic;
    },
    setFileHidden: (
      state,
      action: PayloadAction<{
        stage: StageType;
        fileName: string;
        hidden: boolean;
      }>,
    ) => {
      const { stage, fileName, hidden } = action.payload;
      state.fileHidden[stage][fileName] = hidden;
    },
    setReceiveNotifications: (state, action: PayloadAction<boolean>) => {
      state.receiveNotifications = action.payload;
    },
    toggleHiddenTerritoryObject(
      state,
      action: PayloadAction<{ stage: StageType; type: "stage" | "building" }>,
    ) {
      const { stage, type } = action.payload;
      state.hiddenTerritories[type][stage] =
        !state.hiddenTerritories[type][stage];
    },
    addDrawnGraphic(state, action: PayloadAction<Graphic>) {
      state.drawnGraphics.push(action.payload);
    },
    removeDrawnGraphic(state, action: PayloadAction<Graphic>) {
      state.drawnGraphics = state.drawnGraphics.filter(
        (g) => g !== action.payload,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUtilities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchUtilities.fulfilled,
        (
          state,
          action: PayloadAction<{
            items: ProjectsResponse[];
            isContractor: boolean;
          }>,
        ) => {
          state.isLoading = false;
          const { items, isContractor } = action.payload;
          state.rawData = items;
          const formattedData = items.map((item) =>
            formatProject(item, isContractor),
          );
          state.fullData = sortProjects(formattedData, isContractor);
          state.notificationCount = items.filter(
            (project) => project.notificationId,
          ).length;
        },
      )
      .addCase(fetchUtilities.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchProjectFiles.pending, (state) => {
        state.isLoadingProjectFiles = true;
      })
      .addCase(fetchProjectFiles.fulfilled, (state) => {
        state.isLoadingProjectFiles = false;
      })
      .addCase(fetchProjectFiles.rejected, (state) => {
        state.isLoadingProjectFiles = false;
      })
      .addCase(
        fetchProjectLogs.fulfilled,
        (state, action: PayloadAction<ProjectLog[]>) => {
          state.projectLog = action.payload;
        },
      )
      .addCase(editFeaturesOnMap, (state, action) => {
        if (!state.convertedGeometry) return;

        const geometryMap = action.payload as Map<number, any>;

        for (const stage of stages) {
          const stageGeometry = state.convertedGeometry[stage];
          if (!stageGeometry) continue;

          for (const layerData of Object.values(stageGeometry)) {
            layerData.graphics.forEach((graphic: any) => {
              const id = graphic.attributes?.uuid;
              if (geometryMap.has(id)) {
                const geometryObj = geometryMap.get(id);
                const parsedGeometry = JSON.parse(graphic.geometry);
                const wkid = parsedGeometry?.spatialReference?.wkid;
                const projectedGeometry = projection.project(
                  geometryObj,
                  new SpatialReference({ wkid }),
                );
                graphic.geometry = JSON.stringify(projectedGeometry);
              }
            });
          }
        }
      });
  },
});

export const {
  setData,
  setIsMapReadyUtilities,
  setSelectedRow,
  setUploadedFiles,
  setConvertedGeometry,
  removeUploadedFileAndGeometry,
  setSelectedFeature,
  setVisibleEditAttributes,
  updateFeatureAttributes,
  resetEditAttributesState,
  resetTableState,
  removeGraphicFromConvertedGeometry,
  setStageTerritoryGeometry,
  setBuildingTerritoryGeometry,
  resetMasterUtilitiesState,
  setMainOperationFeature,
  setSecondaryOperationFeature,
  updateConvertedGeometryFeature,
  updateStageTerritoryFeature,
  setFileHidden,
  updateBuildingTerritoryFeature,
  setReceiveNotifications,
  toggleHiddenTerritoryObject,
  addDrawnGraphic,
  removeDrawnGraphic,
  setFilesLoadedFromServer,
} = masterUtilitiesSlice.actions;

export default masterUtilitiesSlice.reducer;
